/**
 * Deadlines fuer Aufgaben, Projekte, Ziele und Key Results (Roadmap F-09).
 *
 * Rein und ohne Dexie. Deadline heisst: BIS WANN es fertig sein muss. Das ist
 * etwas anderes als der eingeplante Zeitpunkt, an dem man daran arbeitet
 * (Tagesplan, Zeitblock im Kalender) - beide Felder bestehen getrennt.
 *
 * Eine Deadline ist ein Datum, optional mit Uhrzeit. Ohne Uhrzeit gilt sie
 * bis zum Ende des Tages - deshalb `23:59` als Vergleichswert.
 */
import { differenceInDays } from '@/lib/datetime';
import { keyResultProgress } from '@/domain/goals/progress';
import { goalPriority, goalPriorityKey, projectPriority } from '@/domain/priority';
import type { CalendarEvent, Goal, ID, LocalDate, LocalDateTime, PriorityKey, Project, Task } from '@/domain/types';

export type DeadlineKind = 'aufgabe' | 'projekt' | 'ziel' | 'keyresult' | 'klausur';

export const DEADLINE_KIND_LABEL: Record<DeadlineKind, string> = {
  aufgabe: 'Aufgabe',
  projekt: 'Projekt',
  ziel: 'Ziel',
  keyresult: 'Key Result',
  klausur: 'Klausur',
};

export interface DeadlineItem {
  kind: DeadlineKind;
  /** ID des Eintrags; bei Key Results die des Key Results. */
  id: ID;
  /** Wohin ein Klick fuehrt: bei Key Results das Ziel, sonst der Eintrag selbst. */
  ownerId: ID;
  /** Uebergeordnete Ebene (F-11): Projekt oder Ziel einer Aufgabe, Ziel eines Projekts. */
  parent?: { kind: 'projekt' | 'ziel'; id: ID };
  /** Prioritaet des Projekts (V-04) - im Kalender als Punkt an der Fahne. */
  priority?: PriorityKey;
  title: string;
  date: LocalDate;
  time?: string;
  areaId: ID | null;
}

/** Ab so vielen Tagen Rest gilt eine Deadline als "bald". */
export const BALD_TAGE = 3;

export type DeadlineState = 'ueberfaellig' | 'heute' | 'bald' | 'spaeter';

export interface DeadlineStatus {
  state: DeadlineState;
  /** Tage bis zur Deadline; negativ = ueberfaellig. */
  days: number;
  /** Fertiger deutscher Text, z. B. "noch 3 Tage", "überfällig seit 2 Tagen". */
  label: string;
}

/** Vergleichswert: Deadline ohne Uhrzeit gilt bis zum Ende des Tages. */
export function deadlineKey(date: LocalDate, time?: string): LocalDateTime {
  return `${date}T${time ?? '23:59'}`;
}

export function deadlineStatus(date: LocalDate, time: string | undefined, jetzt: LocalDateTime): DeadlineStatus {
  const heute = jetzt.slice(0, 10);
  const days = differenceInDays(heute, date);
  const bis = time ? ` bis ${time}` : '';

  if (days < 0) {
    const n = -days;
    return { state: 'ueberfaellig', days, label: `überfällig seit ${n} ${n === 1 ? 'Tag' : 'Tagen'}` };
  }
  if (days === 0) {
    if (time && deadlineKey(date, time) < jetzt) {
      return { state: 'ueberfaellig', days, label: `überfällig seit ${time}` };
    }
    return { state: 'heute', days, label: time ? `heute bis ${time}` : 'heute fällig' };
  }
  if (days === 1) return { state: 'bald', days, label: `morgen${bis}` };
  return { state: days <= BALD_TAGE ? 'bald' : 'spaeter', days, label: `noch ${days} Tage` };
}

const offeneAufgabe = (t: Task) => t.status !== 'erledigt';
const offenesProjekt = (p: Project) => p.status !== 'abgeschlossen';
const offenesZiel = (g: Goal) => g.status === 'offen' || g.status === 'laeuft';

/**
 * Alle Deadlines, die noch zaehlen. Erledigtes, Abgeschlossenes und
 * Erreichtes faellt heraus - eine erledigte Aufgabe hat keine Frist mehr.
 * Ebenso ein Key Result, das seinen Zielwert schon erreicht hat.
 */
export function collectDeadlines(input: {
  tasks: Task[]; projects: Project[]; goals: Goal[];
  /** Klausuren (F-22) - Termine mit `klausur`; nur die uebergebenen zaehlen. */
  exams?: CalendarEvent[];
}): DeadlineItem[] {
  const items: DeadlineItem[] = [];

  for (const t of input.tasks) {
    if (!t.dueDate || !offeneAufgabe(t)) continue;
    const parent = t.projectId ? { kind: 'projekt' as const, id: t.projectId }
      : t.goalId ? { kind: 'ziel' as const, id: t.goalId } : undefined;
    items.push({ kind: 'aufgabe', id: t.id, ownerId: t.id, parent, title: t.title, date: t.dueDate, time: t.dueTime, areaId: t.areaId });
  }
  for (const p of input.projects) {
    if (!p.deadline || !offenesProjekt(p)) continue;
    const parent = p.goalId ? { kind: 'ziel' as const, id: p.goalId } : undefined;
    items.push({ kind: 'projekt', id: p.id, ownerId: p.id, parent, priority: projectPriority(p), title: p.title, date: p.deadline, time: p.deadlineTime, areaId: p.areaId });
  }
  for (const g of input.goals) {
    if (!offenesZiel(g)) continue;
    if (g.deadline) {
      items.push({ kind: 'ziel', id: g.id, ownerId: g.id, priority: goalPriorityKey(goalPriority(g)), title: g.title, date: g.deadline, time: g.deadlineTime, areaId: g.areaId });
    }
    for (const kr of g.keyResults) {
      if (!kr.deadline || keyResultProgress(kr) >= 1) continue;
      items.push({ kind: 'keyresult', id: kr.id, ownerId: g.id, parent: { kind: 'ziel', id: g.id }, title: kr.title || g.title, date: kr.deadline, time: kr.deadlineTime, areaId: g.areaId });
    }
  }

  for (const e of input.exams ?? []) {
    if (!e.klausur || e.allDay) continue;
    const start = String(e.start);
    items.push({ kind: 'klausur', id: e.id, ownerId: e.id, title: e.title, date: start.slice(0, 10), time: start.slice(11, 16), areaId: e.areaId });
  }

  return items.sort((a, b) => deadlineKey(a.date, a.time).localeCompare(deadlineKey(b.date, b.time)));
}

export interface DeadlineConflict {
  child: DeadlineItem;
  parent: DeadlineItem;
}

/**
 * Widerspruechliche Fristen: eine untergeordnete Ebene endet spaeter als die
 * uebergeordnete (Roadmap-Vorschlag zu F-09). Aufgabe nach ihrem Projekt,
 * Aufgabe nach ihrem Ziel, Projekt nach seinem Ziel, Key Result nach seinem
 * Ziel. Nur offene Eintraege - siehe `collectDeadlines`.
 */
export function deadlineConflicts(input: {
  tasks: Task[]; projects: Project[]; goals: Goal[];
}): DeadlineConflict[] {
  const items = collectDeadlines(input);
  const byId = new Map(items.filter((i) => i.kind !== 'keyresult').map((i) => [i.id, i]));
  const konflikte: DeadlineConflict[] = [];

  const pruefe = (child: DeadlineItem, parentId: ID | undefined) => {
    const parent = parentId ? byId.get(parentId) : undefined;
    if (parent && deadlineKey(child.date, child.time) > deadlineKey(parent.date, parent.time)) {
      konflikte.push({ child, parent });
    }
  };

  for (const item of items) {
    if (item.kind !== 'ziel') pruefe(item, item.parent?.id);
  }
  return konflikte;
}
